/**
 * <CierreDetalleModal> — Matriz "qué ve cada propiedad en qué meses".
 *
 * Complementa a <CierreMensualCard>: la tarjeta muestra el estado del mes
 * seleccionado; este modal despliega una matriz clara PROPIEDAD × MES con el
 * estado efectivo de visibilidad al cliente (Visible / Oculto), para que el
 * admin vea de un golpe qué meses/propiedades siguen sin publicar.
 *
 * El estado efectivo espeja la regla del backend (`publishedPropIdsForPeriodo`):
 *   - fila explícita publicado=true  → Visible
 *   - fila explícita publicado=false → Oculto
 *   - sin fila y mes < mes actual    → Visible (histórico, por defecto)
 *   - sin fila y mes actual/futuro   → Oculto (espera tu publicación)
 *
 * Ver docs/flujo-financiero.md §13.
 */

import { useEffect, useMemo, useState } from "react";
import { Button, Modal, Table, Tag, Typography } from "antd";
import type { Dayjs } from "dayjs";
import { finanzasEmpresaApi } from "../../services/finanzasEmpresa.api";
import { formatPropertyName } from "../../utils/propertyDisplay";
import { mesActualChile } from "../../utils/fechaChile";

const { Text } = Typography;

const MESES_ABREV = [
  "ene", "feb", "mar", "abr", "may", "jun",
  "jul", "ago", "sep", "oct", "nov", "dic",
];

function monthLabel(mes: string): string {
  const [y, m] = mes.split("-").map(Number);
  return `${MESES_ABREV[(m || 1) - 1]} ${String(y).slice(2)}`;
}

/**
 * 'YYYY-MM' del mes actual EN HORA CHILENA.
 *
 * Antes salía de `new Date()`, o sea del reloj del DISPOSITIVO. Con el equipo
 * en una zona por delante de Chile —de viaje, o mal configurado en UTC, que
 * desde las 20:00 chilenas ya está en el día siguiente— el último día del mes
 * la matriz adelantaba el mes: mostraba el mes en curso como cerrado y visible
 * al cliente cuando el backend todavía no lo había publicado. Round 2026-08-18.
 */
function currentMonthStr(): string {
  return mesActualChile();
}

interface CierreDetalleModalProps {
  open: boolean;
  onClose: () => void;
  propiedades: { id: number; nombre: string }[];
  /** Mes ancla (el seleccionado en la página). La matriz muestra este mes y los anteriores. */
  anchorMonth: Dayjs;
}

export function CierreDetalleModal({
  open,
  onClose,
  propiedades,
  anchorMonth,
}: CierreDetalleModalProps) {
  // Cuántos meses mostrar (terminando en el mes ancla). "Ver más" agrega 6.
  const [monthsCount, setMonthsCount] = useState(6);
  // Cache mes -> Map<propId, publicado> (SOLO filas explícitas de cierres_cliente).
  const [cache, setCache] = useState<Record<string, Map<number, boolean>>>({});
  const [loading, setLoading] = useState(false);

  const currentMes = currentMonthStr();

  // Meses a mostrar: `monthsCount` terminando en el ancla (viejo → nuevo).
  const months = useMemo(() => {
    const arr: string[] = [];
    for (let i = monthsCount - 1; i >= 0; i--) {
      arr.push(anchorMonth.subtract(i, "month").format("YYYY-MM"));
    }
    return arr;
  }, [anchorMonth, monthsCount]);

  // Carga los meses que falten en cache (una llamada por mes, en paralelo).
  useEffect(() => {
    if (!open) return;
    const missing = months.filter((m) => !(m in cache));
    if (!missing.length) return;
    let cancelled = false;
    setLoading(true);
    void Promise.all(
      missing.map(async (mes) => {
        const res = await finanzasEmpresaApi.getCierresMes(mes);
        const map = new Map<number, boolean>();
        for (const r of ((res as any)?.rows ?? [])) {
          map.set(Number(r.id_propiedad), Boolean(r.publicado));
        }
        return [mes, map] as const;
      }),
    ).then((results) => {
      if (cancelled) return;
      setCache((prev) => {
        const next = { ...prev };
        for (const [mes, map] of results) next[mes] = map;
        return next;
      });
      setLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [open, months, cache]);

  // Estado efectivo de una (propiedad, mes) según la regla canónica.
  const esVisible = (propId: number, mes: string): boolean => {
    const map = cache[mes];
    if (map && map.has(propId)) return Boolean(map.get(propId));
    return mes < currentMes; // sin fila: histórico visible; actual/futuro oculto
  };

  // Resumen por mes (cuántas propiedades visibles) para el encabezado.
  const visiblesPorMes = useMemo(() => {
    const out: Record<string, number> = {};
    for (const mes of months) {
      out[mes] = propiedades.reduce(
        (acc, p) => acc + (esVisible(p.id, mes) ? 1 : 0),
        0,
      );
    }
    return out;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [months, propiedades, cache]);

  const propsOrdenadas = useMemo(
    () =>
      [...propiedades].sort((a, b) =>
        String(a.nombre ?? "").localeCompare(String(b.nombre ?? "")),
      ),
    [propiedades],
  );

  const total = propiedades.length;

  const columns = useMemo(() => {
    const cols: any[] = [
      {
        title: "Propiedad",
        dataIndex: "nombre",
        key: "nombre",
        fixed: "left" as const,
        width: 190,
        render: (n: string) => (
          <span style={{ fontWeight: 600 }}>{formatPropertyName(n)}</span>
        ),
      },
    ];
    for (const mes of months) {
      const esActual = mes === currentMes;
      const n = visiblesPorMes[mes] ?? 0;
      cols.push({
        key: mes,
        align: "center" as const,
        width: 96,
        onHeaderCell: () =>
          esActual
            ? { style: { background: "#ede9fe" } }
            : ({} as any),
        title: (
          <div style={{ lineHeight: 1.25 }}>
            <div
              style={{
                fontWeight: esActual ? 800 : 600,
                textTransform: "capitalize",
                color: esActual ? "#6d28d9" : undefined,
              }}
            >
              {monthLabel(mes)}
            </div>
            <div style={{ fontSize: 11, color: "#8c8c8c", fontWeight: 500 }}>
              {n}/{total}
            </div>
          </div>
        ),
        render: (_: any, row: { id: number }) =>
          esVisible(row.id, mes) ? (
            <Tag color="green" style={{ margin: 0 }}>
              Visible
            </Tag>
          ) : (
            <Tag color="red" style={{ margin: 0 }}>
              Oculto
            </Tag>
          ),
      });
    }
    return cols;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [months, visiblesPorMes, cache, total]);

  const dataSource = useMemo(
    () => propsOrdenadas.map((p) => ({ key: p.id, id: p.id, nombre: p.nombre })),
    [propsOrdenadas],
  );

  return (
    <Modal
      open={open}
      title="¿Qué ve cada propiedad y en qué meses?"
      onCancel={onClose}
      footer={<Button onClick={onClose}>Cerrar</Button>}
      width={Math.min(980, 320 + months.length * 96)}
      centered
    >
      <Text type="secondary" style={{ fontSize: 13 }}>
        Estado de visibilidad al cliente por propiedad y mes. El mes en curso
        aparece resaltado. <strong>Visible</strong> = el cliente ve ese mes en su
        panel de finanzas; <strong>Oculto</strong> = no lo ve (sin publicar).
      </Text>

      <Table
        style={{ marginTop: 12 }}
        size="small"
        columns={columns}
        dataSource={dataSource}
        loading={loading}
        pagination={false}
        scroll={{ x: 190 + months.length * 96, y: 420 }}
      />

      <div
        style={{
          marginTop: 12,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: 8,
        }}
      >
        <div style={{ fontSize: 12, color: "#595959" }}>
          <Tag color="green" style={{ margin: "0 6px 0 0" }}>
            Visible
          </Tag>
          <Tag color="red" style={{ margin: "0 10px 0 0" }}>
            Oculto
          </Tag>
          Los meses anteriores al actual son <strong>visibles por defecto</strong>
          ; el mes en curso y los futuros esperan que los publiques.
        </div>
        <Button
          size="small"
          onClick={() => setMonthsCount((c) => c + 6)}
          disabled={loading}
        >
          Ver 6 meses anteriores
        </Button>
      </div>
    </Modal>
  );
}
